import { useEffect, useRef, useState } from 'react';
import { useWeather } from '../contexts/WeatherContext';
import { Location } from '../types';

// Weather particle types
type ParticleType = 'rain' | 'snow';
type WeatherType = 'clear' | 'clouds' | 'rain' | 'snow' | 'thunder';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  type: ParticleType;
  length?: number;
  radius?: number;
  color: string;
  width?: number;
}

const WeatherVisualization = () => {
  const { forecast, location, setLocation } = useWeather();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [currentWeather, setCurrentWeather] = useState<WeatherType>('clear');
  const [isThundering, setIsThundering] = useState(false);
  const [cityInput, setCityInput] = useState(location?.display_name || 'Tokyo');
  const [isSearching, setIsSearching] = useState(false);
  const particlesRef = useRef<Particle[]>([]);
  const animationFrameRef = useRef<number>();

  // Map forecast conditions to visual weather types
  const mapConditionToWeather = (condition: string): WeatherType => {
    const cond = condition.toLowerCase();
    if (cond.includes('rain') || cond.includes('storm')) return 'rain';
    if (cond.includes('snow')) return 'snow';
    if (cond.includes('cloud')) return 'clouds';
    if (cond.includes('thunder')) return 'thunder';
    return 'clear';
  };

  // Initialize particles based on weather type
  const initParticles = (type: ParticleType, count: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const width = canvas.width;
    const height = canvas.height;
    const particles: Particle[] = [];

    for (let i = 0; i < count; i++) {
      const particle: Particle = {
        x: Math.random() * width,
        y: Math.random() * height,
        vx: 0,
        vy: 0,
        type,
        color: '',
      };

      if (type === 'rain') {
        particle.vx = (Math.random() - 0.5) * 2 + 2;
        particle.vy = Math.random() * 10 + 20;
        particle.length = Math.random() * 20 + 10;
        particle.color = 'rgba(174, 194, 224, 0.6)';
        particle.width = 1.5;
      } else if (type === 'snow') {
        particle.vx = (Math.random() - 0.5) * 4;
        particle.vy = Math.random() * 2 + 1;
        particle.radius = Math.random() * 3 + 1;
        particle.color = 'rgba(255, 255, 255, 0.8)';
      }

      particles.push(particle);
    }

    particlesRef.current = particles;
  };

  // Update and draw particles
  const updateParticles = (ctx: CanvasRenderingContext2D, width: number, height: number) => {
    ctx.clearRect(0, 0, width, height);

    particlesRef.current.forEach((p) => {
      // Update position
      p.x += p.vx;
      p.y += p.vy;

      // Wind effect for snow
      if (p.type === 'snow') {
        p.x += Math.sin(Date.now() * 0.001 + p.y * 0.01) * 0.5;
      }

      // Reset if out of bounds
      if (p.y > height || p.x > width || p.x < -50) {
        p.y = -20;
        p.x = Math.random() * width;
      }

      // Draw particle
      ctx.fillStyle = p.color;
      ctx.beginPath();

      if (p.type === 'rain' && p.length && p.width) {
        ctx.strokeStyle = p.color;
        ctx.lineWidth = p.width;
        ctx.moveTo(p.x, p.y);
        ctx.lineTo(p.x + p.vx * 0.5, p.y + p.length);
        ctx.stroke();
      } else if (p.type === 'snow' && p.radius) {
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fill();
      }
    });
  };

  // Animation loop
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    resize();
    window.addEventListener('resize', resize);

    const animate = () => {
      updateParticles(ctx, canvas.width, canvas.height);
      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resize);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  // Update weather visualization when forecast changes
  useEffect(() => {
    if (forecast?.current) {
      const weatherType = mapConditionToWeather(forecast.current.condition);
      setCurrentWeather(weatherType);

      // Initialize particles based on weather
      if (weatherType === 'rain' || weatherType === 'thunder') {
        initParticles('rain', weatherType === 'thunder' ? 800 : 600);
        setIsThundering(weatherType === 'thunder');
      } else if (weatherType === 'snow') {
        initParticles('snow', 800);
        setIsThundering(false);
      } else {
        particlesRef.current = [];
        setIsThundering(false);
      }
    }
  }, [forecast]);

  // Lightning effect
  useEffect(() => {
    if (!isThundering) return;

    const flashInterval = setInterval(() => {
      if (Math.random() > 0.7) {
        const flash = document.getElementById('lightning-flash');
        if (flash) {
          flash.style.opacity = String(Math.random() * 0.5 + 0.3);
          setTimeout(() => {
            flash.style.opacity = '0';
          }, 50 + Math.random() * 100);
        }
      }
    }, 500);

    return () => clearInterval(flashInterval);
  }, [isThundering]);

  // Search for city
  const handleSearch = async () => {
    if (!cityInput.trim()) return;

    setIsSearching(true);
    try {
      // Geocode the city
      const geoRes = await fetch(
        `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(
          cityInput
        )}&count=1&language=en&format=json`
      );
      const geoData = await geoRes.json();

      if (geoData.results && geoData.results.length > 0) {
        const { latitude, longitude, name } = geoData.results[0];
        const newLocation: Location = {
          latitude,
          longitude,
          display_name: name,
          timezone: 'UTC'
        };
        setLocation(newLocation);
      } else {
        alert('City not found!');
      }
    } catch (error) {
      console.error('Search error:', error);
      alert('Error fetching weather data');
    } finally {
      setIsSearching(false);
    }
  };

  // Get background gradient based on weather
  const getBackgroundGradient = (): string => {
    switch (currentWeather) {
      case 'clear':
        return 'linear-gradient(to bottom, #2980b9, #6dd5fa)';
      case 'clouds':
        return 'linear-gradient(to bottom, #757f9a, #d7dde8)';
      case 'rain':
        return 'linear-gradient(to bottom, #203a43, #2c5364)';
      case 'snow':
        return 'linear-gradient(to bottom, #83a4d4, #b6fbff)';
      case 'thunder':
        return 'linear-gradient(to bottom, #0f2027, #203a43, #2c5364)';
      default:
        return 'linear-gradient(to bottom, #1e3c72, #2a5298)';
    }
  };

  // Get weather emoji
  const getWeatherEmoji = (): string => {
    switch (currentWeather) {
      case 'clear':
        return '☀️';
      case 'clouds':
        return '☁️';
      case 'rain':
        return '🌧️';
      case 'snow':
        return '❄️';
      case 'thunder':
        return '⚡';
      default:
        return '☀️';
    }
  };

  // Get weather description
  const getWeatherDescription = (): string => {
    switch (currentWeather) {
      case 'clear':
        return 'Clear Sky';
      case 'clouds':
        return 'Overcast';
      case 'rain':
        return 'Heavy Storm';
      case 'snow':
        return 'Blizzard';
      case 'thunder':
        return 'Thunderstorm';
      default:
        return 'Loading...';
    }
  };

  return (
    <div className="relative w-full h-screen overflow-hidden">
      {/* Sky Gradient Background */}
      <div
        id="sky-gradient"
        className="absolute inset-0 transition-all duration-[2s] ease-in-out"
        style={{ background: getBackgroundGradient() }}
      />

      {/* Sun */}
      {currentWeather === 'clear' && (
        <div
          id="sun"
          className="absolute top-[10%] right-[10%] w-[120px] h-[120px] rounded-full transition-opacity duration-1000 animate-pulse"
          style={{
            background: 'radial-gradient(circle, #ffd700 20%, rgba(255, 215, 0, 0) 70%)',
            filter: 'blur(10px)',
            boxShadow: '0 0 60px #ffaa00',
          }}
        />
      )}

      {/* Clouds Container */}
      {currentWeather === 'clouds' && (
        <div
          id="clouds-container"
          className="absolute inset-0 opacity-100 transition-opacity duration-[2s] pointer-events-none"
        >
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className="absolute bg-white rounded-full opacity-80"
              style={{
                width: `${150 + Math.random() * 300}px`,
                height: `${50 + Math.random() * 80}px`,
                top: `${Math.random() * 40}%`,
                left: `-${Math.random() * 500}px`,
                filter: 'blur(20px)',
                boxShadow: '0 0 40px #fff',
                animation: `floatCloud ${30 + Math.random() * 40}s linear infinite`,
                animationDelay: `-${Math.random() * 60}s`,
              }}
            />
          ))}
        </div>
      )}

      {/* Weather Canvas (particles) */}
      <canvas ref={canvasRef} className="absolute inset-0 pointer-events-none z-10" />

      {/* Lightning Flash */}
      <div
        id="lightning-flash"
        className="absolute inset-0 bg-white opacity-0 pointer-events-none mix-blend-overlay transition-opacity z-20"
      />

      {/* UI Layer */}
      <div className="absolute inset-0 flex flex-col items-center justify-between p-6 md:p-12 text-white z-30">
        {/* Search Bar */}
        <div className="glass-panel rounded-2xl p-4 w-full max-w-md flex gap-2">
          <input
            type="text"
            value={cityInput}
            onChange={(e) => setCityInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            placeholder="Search city..."
            className="bg-transparent border-none outline-none text-white placeholder-gray-300 w-full px-2"
          />
          <button
            onClick={handleSearch}
            disabled={isSearching}
            className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50"
          >
            {isSearching ? '...' : 'Search'}
          </button>
        </div>

        {/* Main Weather Display */}
        <div className="flex flex-col items-center text-center mt-8 mb-auto">
          <div className="text-6xl mb-4">{getWeatherEmoji()}</div>
          <h1 className="text-5xl md:text-7xl font-bold tracking-tighter drop-shadow-lg">
            {location?.display_name?.toUpperCase() || 'LOADING...'}
          </h1>
          <p className="text-xl md:text-2xl opacity-90 font-light mt-2 uppercase tracking-widest drop-shadow-md">
            {forecast ? getWeatherDescription() : 'Fetching...'}
          </p>

          {forecast?.current && (
            <div className="mt-6 flex gap-8">
              <div className="text-center drop-shadow-md">
                <span className="block text-4xl font-bold">
                  {Math.round(forecast.current.temperature_celsius)}°
                </span>
                <span className="text-xs uppercase opacity-70">Temp</span>
              </div>
              <div className="text-center drop-shadow-md">
                <span className="block text-4xl font-bold">
                  {forecast.current.humidity_percent}%
                </span>
                <span className="text-xs uppercase opacity-70">Humidity</span>
              </div>
              <div className="text-center drop-shadow-md">
                <span className="block text-4xl font-bold">
                  {Math.round(forecast.current.wind_speed_ms * 3.6)}km
                </span>
                <span className="text-xs uppercase opacity-70">Wind</span>
              </div>
            </div>
          )}
        </div>

        {/* Weather Type Override (for demonstration) */}
        <div className="glass-panel rounded-2xl p-4 w-full max-w-2xl">
          <p className="text-xs uppercase opacity-60 mb-3 text-center font-bold tracking-wider">
            Visual Override
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {(['clear', 'clouds', 'rain', 'snow', 'thunder'] as WeatherType[]).map((type) => (
              <button
                key={type}
                onClick={() => {
                  setCurrentWeather(type);
                  if (type === 'rain' || type === 'thunder') {
                    initParticles('rain', type === 'thunder' ? 800 : 600);
                    setIsThundering(type === 'thunder');
                  } else if (type === 'snow') {
                    initParticles('snow', 800);
                    setIsThundering(false);
                  } else {
                    particlesRef.current = [];
                    setIsThundering(false);
                  }
                }}
                className={`weather-btn px-4 py-2 rounded-lg text-sm transition-all hover:-translate-y-0.5 hover:shadow-lg ${
                  type === 'clear'
                    ? 'bg-yellow-500/20 border border-yellow-500/30'
                    : type === 'clouds'
                    ? 'bg-gray-500/20 border border-gray-500/30'
                    : type === 'rain'
                    ? 'bg-blue-600/20 border border-blue-600/30'
                    : type === 'snow'
                    ? 'bg-indigo-300/20 border border-indigo-300/30'
                    : 'bg-purple-600/20 border border-purple-600/30'
                }`}
              >
                {type === 'clear'
                  ? '☀️ Clear'
                  : type === 'clouds'
                  ? '☁️ Cloudy'
                  : type === 'rain'
                  ? '🌧️ Storm'
                  : type === 'snow'
                  ? '❄️ Blizzard'
                  : '⚡ Thunder'}
              </button>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        .glass-panel {
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          border: 1px solid rgba(255, 255, 255, 0.2);
          box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.37);
        }

        @keyframes floatCloud {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100vw); }
        }
      `}</style>
    </div>
  );
};

export default WeatherVisualization;
